import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, Briefcase, Building2, Linkedin, Instagram, Globe,
  Mail, Phone, MessageSquare, Facebook, Twitter, Award, Trophy, Quote,
  Package, Send, Pencil, X, Plus, Trash2, Share2, Copy, Check,
} from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";

export default function MemberProfile() {
  const { id } = useParams();
  const { user, updateProfile } = useAuth();
  const [m, setM] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);

  const isOwn = !!user && m && (user.id === m.id);

  useEffect(() => {
    api.get(`/members/${id}`).then(r => setM(r.data)).catch(() => setM(false));
  }, [id]);

  const sendRequest = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in to contact members"); return; }
    setSending(true);
    try {
      await api.post("/referrals", {
        to_member_id: id,
        contact_name: user.name,
        contact_phone: user.phone || "—",
        contact_email: user.email,
        notes: `[Profile contact request] ${msg}`,
        value: 0,
      });
      toast.success("Request sent. We'll notify the member.");
      setMsg("");
    } catch {
      toast.error("Could not send. Try again.");
    } finally { setSending(false); }
  };

  const role = user?.role === "admin" ? "admin" : "member";

  if (m === null) return (
    <DashboardShell role={role} title="Member Profile">
      <div className="py-24 text-center text-plumSoft">Loading…</div>
    </DashboardShell>
  );
  if (m === false) return (
    <DashboardShell role={role} title="Member Profile">
      <div className="py-24 text-center text-plumSoft">Member not found.</div>
    </DashboardShell>
  );

  const contactBtns = buildContactButtons(m);

  return (
    <DashboardShell
      role={role}
      title={m.name || "Member Profile"}
      subtitle={m.business || "WOWSHE community member"}
      actions={(
        <div className="flex items-center gap-2">
          <button data-testid="share-profile-btn" onClick={() => setSharing(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-aubergine/20 text-aubergine text-xs uppercase tracking-widest hover:bg-aubergine hover:text-ivory transition-all">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          {isOwn && (
            <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest hover:bg-aubergineLight">
              <Pencil className="w-3.5 h-3.5" /> Edit profile
            </button>
          )}
        </div>
      )}
    >
      <div data-testid="member-profile">
        <Link to="/directory" className="text-sm text-plum flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to directory</Link>

        {/* HERO — avatar + identity + contact strip */}
        <section className="bg-white rounded-2xl border border-aubergine/8 shadow-luxe overflow-hidden mb-6">
          <div className="h-28 bg-gradient-to-r from-aubergine via-plum to-rose relative">
            <div className="absolute inset-0 grain opacity-30" />
          </div>
          <div className="px-6 md:px-10 pb-7 relative">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl overflow-hidden bg-lavender border-4 border-white shadow-luxe shrink-0 -mt-20 md:-mt-16">
                {m.avatar
                  ? <img
                      src={resolveMediaUrl(m.avatar)}
                      alt={m.name}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  : null}
                <div className={`w-full h-full flex items-center justify-center text-plum font-display text-5xl ${m.avatar ? "hidden" : ""}`}>
                  {m.name?.[0]?.toUpperCase() || "W"}
                </div>
              </div>
              {/* Identity */}
              <div className="flex-1 min-w-0 pt-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-3xl md:text-4xl text-aubergine">{m.name}</h2>
                  {m.membership_status === "annual" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-champagne/20 text-[10px] tracking-wider uppercase text-aubergine">
                      <Award className="w-3 h-3" /> Annual Member
                    </span>
                  )}
                </div>
                <p className="text-plumSoft mt-1">{m.business || "—"}</p>
                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-plum">
                  {m.industry && <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />{m.industry}</span>}
                  {m.city && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{m.city}</span>}
                  {m.chapter && <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{m.chapter}</span>}
                </div>
              </div>
            </div>

            {/* Contact strip */}
            {contactBtns.length > 0 && (
              <div className="mt-6 pt-6 border-t border-aubergine/8">
                <p className="overline mb-3">Get in touch</p>
                <div className="flex flex-wrap gap-2.5">
                  {contactBtns.map((b) => (
                    <a
                      key={b.key}
                      href={b.href}
                      target={b.external ? "_blank" : undefined}
                      rel={b.external ? "noreferrer" : undefined}
                      data-testid={`contact-${b.key}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-aubergine/15 text-aubergine text-xs font-medium hover:bg-aubergine hover:text-ivory hover:border-aubergine transition-all"
                    >
                      <b.Icon className="w-3.5 h-3.5 flex-none" />
                      <span className="truncate max-w-[160px]">{b.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ABOUT */}
        <section className="bg-white rounded-2xl border border-aubergine/8 p-8 mb-6">
          <p className="overline">About</p>
          <h3 className="font-display text-2xl text-aubergine mt-2">{m.name?.split(" ")[0]}'s story</h3>
          <p className="mt-4 text-plumSoft font-light leading-relaxed whitespace-pre-line max-w-4xl">
            {m.bio || "This member has not added a bio yet."}
          </p>
        </section>

        {/* Achievements */}
        {m.achievements?.length > 0 && (
          <section data-testid="achievements-section" className="bg-white rounded-2xl border border-aubergine/8 p-8 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <Trophy className="w-5 h-5 text-rose" />
              <p className="overline !text-rose">Achievements</p>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {m.achievements.map((a, i) => (
                <li key={i} className="flex items-start gap-3 p-4 rounded-xl bg-lavender/40">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose to-champagne text-ivory flex items-center justify-center font-display text-sm shrink-0">{i + 1}</div>
                  <p className="text-sm text-aubergine leading-relaxed pt-1">{a}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Testimonials */}
        {m.testimonials?.length > 0 && (
          <section data-testid="testimonials-section" className="bg-white rounded-2xl border border-aubergine/8 p-8 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <Quote className="w-5 h-5 text-plum" />
              <p className="overline !text-plum">Testimonials</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {m.testimonials.map((t, i) => (
                <article key={i} className="p-5 rounded-xl bg-gradient-to-br from-lavender/60 to-white border border-aubergine/8">
                  <Quote className="w-5 h-5 text-rose/60 mb-3" />
                  <p className="text-sm text-aubergine font-light italic leading-relaxed">"{t.quote}"</p>
                  <div className="mt-4 flex items-center gap-3">
                    {t.avatar ? (
                      <img src={t.avatar} alt={t.author} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-plum/15 text-plum flex items-center justify-center text-sm font-display">{t.author?.[0]}</div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm text-aubergine font-medium truncate">{t.author}</p>
                      {t.role && <p className="text-xs text-plumSoft truncate">{t.role}</p>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Products / Services Catalogue */}
        {m.products?.length > 0 && (
          <section data-testid="catalogue-section" className="bg-white rounded-2xl border border-aubergine/8 p-8 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <Package className="w-5 h-5 text-teal-600" />
              <p className="overline !text-teal-600">Products & Services</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {m.products.map((p, i) => (
                <article key={i} className="rounded-xl overflow-hidden border border-aubergine/8 group hover:shadow-luxe transition-all">
                  {p.image && (
                    <div className="aspect-[16/10] overflow-hidden bg-lavender">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-5">
                    <h4 className="font-display text-lg text-aubergine">{p.name}</h4>
                    {p.price && <p className="text-xs uppercase tracking-widest text-rose mt-1">{p.price}</p>}
                    {p.description && <p className="text-sm text-plumSoft font-light mt-3 leading-relaxed">{p.description}</p>}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Media Gallery — images & videos */}
        {m.gallery?.length > 0 && (
          <section data-testid="gallery-section" className="bg-white rounded-2xl border border-aubergine/8 p-8 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <Package className="w-5 h-5 text-rose" />
              <p className="overline !text-rose">Gallery</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {m.gallery.map((g, i) => (
                <GalleryItem key={i} item={g} />
              ))}
            </div>
          </section>
        )}

        {/* Empty state for own profile */}
        {isOwn && !m.achievements?.length && !m.testimonials?.length && !m.products?.length && !m.gallery?.length && (
          <div className="bg-gradient-to-br from-lavender/60 to-white rounded-2xl border border-dashed border-aubergine/20 p-10 text-center mb-6">
            <Pencil className="w-8 h-8 text-plum mx-auto" />
            <h3 className="font-display text-xl text-aubergine mt-4">Tell the community who you are</h3>
            <p className="text-sm text-plumSoft mt-2 max-w-md mx-auto">Add achievements, peer testimonials, and your products or services so other members can discover, refer and collaborate with you.</p>
            <button onClick={() => setEditing(true)} className="btn-primary mt-5 inline-flex">Complete my profile</button>
          </div>
        )}

        {/* Contact request — hide for own profile */}
        {!isOwn && (
          <section className="bg-white rounded-2xl border border-aubergine/8 p-8 max-w-2xl">
            <p className="overline">Contact request</p>
            <h3 className="font-display text-xl text-aubergine mt-2">Send {m.name?.split(" ")[0]} a message</h3>
            <p className="text-sm text-plumSoft font-light mt-1">
              We'll share your contact details only after they accept.
            </p>
            <form onSubmit={sendRequest} className="mt-5 space-y-3">
              <textarea
                data-testid="contact-msg"
                rows={4}
                required
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Hi! I'd love to connect because..."
                className="input-luxe"
              />
              <button data-testid="send-msg" disabled={sending} className="btn-primary">
                {sending ? "Sending…" : <>Send request <Send className="w-3.5 h-3.5" /></>}
              </button>
            </form>
          </section>
        )}
      </div>

      {editing && (
        <EditProfileModal
          initial={m}
          onClose={() => setEditing(false)}
          onSave={async (payload) => {
            try {
              const updated = await updateProfile(payload);
              setM((prev) => ({ ...prev, ...updated }));
              toast.success("Profile updated.");
              setEditing(false);
            } catch (err) {
              toast.error(formatErr(err.response?.data?.detail) || "Failed to save");
            }
          }}
        />
      )}

      {sharing && <ShareModal member={m} onClose={() => setSharing(false)} />}
    </DashboardShell>
  );
}

function resolveMediaUrl(value) {
  if (!value) return "";
  if (
    value.startsWith("data:") ||
    value.startsWith("blob:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (!base) return value;

  return value.startsWith("/") ? `${base}${value}` : `${base}/${value}`;
}

function buildContactButtons(m) {
  const out = [];
  if (m.phone) out.push({ key: "phone", label: m.phone, href: `tel:${m.phone.replace(/\s/g, "")}`, Icon: Phone });
  if (m.whatsapp) out.push({ key: "whatsapp", label: "WhatsApp", href: `https://wa.me/${m.whatsapp.replace(/[^\d]/g, "")}`, Icon: MessageSquare, external: true });
  if (m.email) out.push({ key: "email", label: m.email, href: `mailto:${m.email}`, Icon: Mail });
  if (m.website) out.push({ key: "website", label: "Website", href: m.website, Icon: Globe, external: true });
  if (m.linkedin) out.push({ key: "linkedin", label: "LinkedIn", href: m.linkedin.startsWith("http") ? m.linkedin : `https://linkedin.com/in/${m.linkedin}`, Icon: Linkedin, external: true });
  if (m.instagram) out.push({ key: "instagram", label: "Instagram", href: m.instagram.startsWith("http") ? m.instagram : `https://instagram.com/${m.instagram}`, Icon: Instagram, external: true });
  if (m.facebook) out.push({ key: "facebook", label: "Facebook", href: m.facebook.startsWith("http") ? m.facebook : `https://facebook.com/${m.facebook}`, Icon: Facebook, external: true });
  if (m.twitter) out.push({ key: "twitter", label: "Twitter / X", href: m.twitter.startsWith("http") ? m.twitter : `https://x.com/${m.twitter}`, Icon: Twitter, external: true });
  return out;
}

export function EditProfileModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    avatar: initial.avatar || "",
    phone: initial.phone || "",
    whatsapp: initial.whatsapp || "",
    website: initial.website || "",
    linkedin: initial.linkedin || "",
    instagram: initial.instagram || "",
    facebook: initial.facebook || "",
    twitter: initial.twitter || "",
    achievements: initial.achievements?.length ? [...initial.achievements] : [""],
    testimonials: initial.testimonials?.length ? initial.testimonials.map((t) => ({ ...t })) : [{ author: "", role: "", quote: "", avatar: "" }],
    products: initial.products?.length ? initial.products.map((p) => ({ ...p })) : [{ name: "", description: "", price: "", image: "" }],
    gallery: Array.isArray(initial.gallery) ? initial.gallery.map((g) => ({ ...g })) : [],
    office_address: initial.office_address || "",
    office_lat: initial.office_lat ?? "",
    office_lng: initial.office_lng ?? "",
    location_visible: !!initial.location_visible,
    company_logo: initial.company_logo || "",
    gstin: initial.gstin || "",
  });
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateArr = (k, i, v) => setForm((f) => ({ ...f, [k]: f[k].map((x, idx) => (idx === i ? v : x)) }));
  const addArr = (k, blank) => setForm((f) => ({ ...f, [k]: [...f[k], blank] }));
  const removeArr = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, idx) => idx !== i) }));

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Browser doesn't support geolocation"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, office_lat: pos.coords.latitude.toFixed(6), office_lng: pos.coords.longitude.toFixed(6) }));
        toast.success("Location captured");
        setGeoBusy(false);
      },
      () => { toast.error("Could not get location. Allow permission or enter manually."); setGeoBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const lookupAddress = async () => {
    if (!form.office_address.trim()) { toast("Type an address first"); return; }
    setGeoBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(form.office_address)}`);
      const data = await r.json();
      if (data && data[0]) {
        setForm((f) => ({ ...f, office_lat: parseFloat(data[0].lat).toFixed(6), office_lng: parseFloat(data[0].lon).toFixed(6) }));
        toast.success("Address located");
      } else { toast.error("No match. Try a more specific address."); }
    } catch { toast.error("Lookup failed"); }
    finally { setGeoBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Profile photo must be 2MB or smaller.");
      return;
    }

    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await api.post("/auth/profile/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      updateField("avatar", data.avatar || "");
      toast.success("Profile photo uploaded.");
    } catch (e) {
      toast.error(
        formatErr(e.response?.data?.detail) || "Could not upload profile photo."
      );
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      const { data } = await api.delete("/auth/profile/avatar");
      updateField("avatar", data.avatar || "");
      toast.success("Profile photo removed.");
    } catch (e) {
      toast.error(
        formatErr(e.response?.data?.detail) || "Could not remove profile photo."
      );
    } finally {
      setAvatarBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      avatar: form.avatar || "",
      phone: form.phone,
      whatsapp: form.whatsapp,
      website: form.website,
      linkedin: form.linkedin,
      instagram: form.instagram,
      facebook: form.facebook,
      twitter: form.twitter,
      achievements: form.achievements.map((a) => (a || "").trim()).filter(Boolean),
      testimonials: form.testimonials.filter((t) => (t.quote || "").trim() && (t.author || "").trim()),
      products: form.products.filter((p) => (p.name || "").trim()),
      gallery: form.gallery
        .filter((g) => (g.url || "").trim())
        .map((g) => ({ type: g.type === "video" ? "video" : "image", url: (g.url || "").trim(), caption: (g.caption || "").trim() })),
      office_address: form.office_address,
      office_lat: form.office_lat === "" ? null : parseFloat(form.office_lat),
      office_lng: form.office_lng === "" ? null : parseFloat(form.office_lng),
      location_visible: form.location_visible,
      company_logo: form.company_logo,
      gstin: form.gstin,
    };
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-aubergine/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-luxe" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-aubergine/8 px-7 py-5 flex items-center justify-between rounded-t-2xl">
          <div>
            <p className="overline">Edit profile</p>
            <h2 className="font-display text-2xl text-aubergine mt-1">Make your profile shine</h2>
          </div>
          <button onClick={onClose} className="text-plum hover:text-aubergine" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-8">
          {/* Profile photo */}
          <section>
            <p className="overline mb-3">Profile photo</p>
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="w-28 h-28 rounded-2xl overflow-hidden bg-lavender border-4 border-white shadow-luxe shrink-0">
                {form.avatar ? (
                  <img
                    src={resolveMediaUrl(form.avatar)}
                    alt={initial.name || "Profile"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : null}
                <div className={`w-full h-full flex items-center justify-center text-plum font-display text-4xl ${form.avatar ? "hidden" : ""}`}>
                  {initial.name?.[0]?.toUpperCase() || "W"}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest cursor-pointer hover:bg-aubergineLight transition-all">
                  <Plus className="w-3.5 h-3.5" />
                  {avatarBusy ? "Uploading…" : "Upload photo"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    disabled={avatarBusy}
                    onChange={(e) => {
                      uploadAvatar(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>

                {form.avatar && (
                  <button
                    type="button"
                    onClick={removeAvatar}
                    disabled={avatarBusy}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-rose/30 text-rose text-xs uppercase tracking-widest hover:bg-rose hover:text-ivory transition-all disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove
                  </button>
                )}

                <p className="basis-full text-[11px] text-plumSoft/70 mt-1">
                  JPG, PNG, WEBP or GIF · maximum 2 MB
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <p className="overline mb-3">Contact & social</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Phone" testid="ep-phone" value={form.phone} onChange={(v) => updateField("phone", v)} placeholder="+91 98765 43210" />
              <Field label="WhatsApp number" testid="ep-whatsapp" value={form.whatsapp} onChange={(v) => updateField("whatsapp", v)} placeholder="+91 98765 43210" />
              <Field label="Website" testid="ep-website" value={form.website} onChange={(v) => updateField("website", v)} placeholder="https://yourbrand.com" />
              <Field label="LinkedIn" testid="ep-linkedin" value={form.linkedin} onChange={(v) => updateField("linkedin", v)} placeholder="username or full URL" />
              <Field label="Instagram" testid="ep-instagram" value={form.instagram} onChange={(v) => updateField("instagram", v)} placeholder="@handle" />
              <Field label="Facebook" testid="ep-facebook" value={form.facebook} onChange={(v) => updateField("facebook", v)} placeholder="username or page URL" />
              <Field label="Twitter / X" testid="ep-twitter" value={form.twitter} onChange={(v) => updateField("twitter", v)} placeholder="@handle" />
            </div>
          </section>

          {/* Office location */}
          <section>
            <p className="overline mb-3">Office location</p>
            <div className="space-y-3">
              <Field label="Address" testid="ep-address" value={form.office_address} onChange={(v) => updateField("office_address", v)} placeholder="HITEC City, Hyderabad" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Latitude" testid="ep-lat" value={form.office_lat} onChange={(v) => updateField("office_lat", v)} placeholder="17.4474" />
                <Field label="Longitude" testid="ep-lng" value={form.office_lng} onChange={(v) => updateField("office_lng", v)} placeholder="78.3762" />
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="ep-use-location" onClick={useCurrentLocation} disabled={geoBusy} className="text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-aubergine/20 text-plum hover:bg-aubergine hover:text-ivory transition-all disabled:opacity-50">
                  {geoBusy ? "Locating…" : "Use my current location"}
                </button>
                <button type="button" data-testid="ep-lookup-address" onClick={lookupAddress} disabled={geoBusy} className="text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-aubergine/20 text-plum hover:bg-aubergine hover:text-ivory transition-all disabled:opacity-50">
                  Find coordinates from address
                </button>
              </div>
              <label className="flex items-center gap-3 mt-3 p-4 rounded-xl bg-lavender/40 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="ep-location-visible"
                  checked={form.location_visible}
                  onChange={(e) => updateField("location_visible", e.target.checked)}
                  className="w-4 h-4 accent-aubergine"
                />
                <span className="text-sm text-aubergine">Show my office on the Members Map <span className="text-plumSoft font-light">(only visible to logged-in members)</span></span>
              </label>
            </div>
          </section>

          {/* Company branding (for invoices & quotations) */}
          <section>
            <p className="overline mb-3">Company branding</p>
            <p className="text-xs text-plumSoft mb-3 font-light">Used automatically when you create invoices & quotations.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company logo URL" testid="ep-company-logo" value={form.company_logo} onChange={(v) => updateField("company_logo", v)} placeholder="https://yourbrand.com/logo.png" />
              <Field label="GSTIN" testid="ep-gstin" value={form.gstin} onChange={(v) => updateField("gstin", v)} placeholder="36ABCDE1234F1Z5" />
            </div>
            {form.company_logo && (
              <div className="mt-3 flex items-center gap-3">
                <span className="overline">Preview:</span>
                <img src={form.company_logo} alt="Company logo" className="h-12 w-auto max-w-[180px] object-contain bg-lavender/40 rounded-lg p-2" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
            )}
          </section>

          {/* Achievements */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Achievements</p>
              <button type="button" data-testid="ep-add-ach" onClick={() => addArr("achievements", "")} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="space-y-2.5">
              {form.achievements.map((a, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input data-testid={`ep-ach-${i}`} value={a} onChange={(e) => updateArr("achievements", i, e.target.value)} placeholder="Award, milestone, recognition…" className="input-luxe flex-1" />
                  <button type="button" onClick={() => removeArr("achievements", i)} className="text-plum hover:text-rose p-2" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Testimonials</p>
              <button type="button" data-testid="ep-add-test" onClick={() => addArr("testimonials", { author: "", role: "", quote: "", avatar: "" })} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="space-y-4">
              {form.testimonials.map((t, i) => (
                <div key={i} className="p-4 rounded-xl bg-lavender/40 border border-aubergine/8 space-y-2.5 relative">
                  <button type="button" onClick={() => removeArr("testimonials", i)} className="absolute top-3 right-3 text-plum hover:text-rose p-1" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Author" testid={`ep-test-author-${i}`} value={t.author} onChange={(v) => updateArr("testimonials", i, { ...t, author: v })} placeholder="Jane Doe" />
                    <Field label="Role / Company" testid={`ep-test-role-${i}`} value={t.role} onChange={(v) => updateArr("testimonials", i, { ...t, role: v })} placeholder="CEO, Acme Co." />
                  </div>
                  <div>
                    <label className="label-luxe">Quote</label>
                    <textarea rows={2} data-testid={`ep-test-quote-${i}`} value={t.quote} onChange={(e) => updateArr("testimonials", i, { ...t, quote: e.target.value })} className="input-luxe" placeholder="What they said about you…" />
                  </div>
                  <Field label="Author avatar URL (optional)" testid={`ep-test-avatar-${i}`} value={t.avatar} onChange={(v) => updateArr("testimonials", i, { ...t, avatar: v })} placeholder="https://…" />
                </div>
              ))}
            </div>
          </section>

          {/* Products */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Products & services</p>
              <button type="button" data-testid="ep-add-prod" onClick={() => addArr("products", { name: "", description: "", price: "", image: "" })} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
            <div className="space-y-4">
              {form.products.map((p, i) => (
                <div key={i} className="p-4 rounded-xl bg-lavender/40 border border-aubergine/8 space-y-2.5 relative">
                  <button type="button" onClick={() => removeArr("products", i)} className="absolute top-3 right-3 text-plum hover:text-rose p-1" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Name" testid={`ep-prod-name-${i}`} value={p.name} onChange={(v) => updateArr("products", i, { ...p, name: v })} placeholder="Service / product name" />
                    <Field label="Price (optional)" testid={`ep-prod-price-${i}`} value={p.price} onChange={(v) => updateArr("products", i, { ...p, price: v })} placeholder="₹15,000 / session" />
                  </div>
                  <div>
                    <label className="label-luxe">Description</label>
                    <textarea rows={2} data-testid={`ep-prod-desc-${i}`} value={p.description} onChange={(e) => updateArr("products", i, { ...p, description: e.target.value })} className="input-luxe" placeholder="What you offer…" />
                  </div>
                  <Field label="Image URL (optional)" testid={`ep-prod-image-${i}`} value={p.image} onChange={(v) => updateArr("products", i, { ...p, image: v })} placeholder="https://…" />
                </div>
              ))}
            </div>
          </section>

          {/* Media Gallery */}
          <section>
            <div className="flex items-center justify-between mb-1">
              <p className="overline">Media gallery — images &amp; videos</p>
              <div className="flex items-center gap-2">
                <button type="button" data-testid="ep-add-image" onClick={() => addArr("gallery", { type: "image", url: "", caption: "" })} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine"><Plus className="w-3.5 h-3.5" /> Image</button>
                <button type="button" data-testid="ep-add-video" onClick={() => addArr("gallery", { type: "video", url: "", caption: "" })} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine"><Plus className="w-3.5 h-3.5" /> Video</button>
              </div>
            </div>
            <p className="text-[11px] text-plumSoft/70 mb-3">Videos: paste a YouTube or Vimeo link — it will be embedded automatically.</p>
            <div className="space-y-4">
              {form.gallery.length === 0 && (
                <p className="text-xs text-plumSoft/70 italic px-1">No media yet. Add images (product shots, event photos) or an intro video.</p>
              )}
              {form.gallery.map((g, i) => (
                <div key={i} className="p-4 rounded-xl bg-lavender/40 border border-aubergine/8 space-y-2.5 relative">
                  <button type="button" onClick={() => removeArr("gallery", i)} className="absolute top-3 right-3 text-plum hover:text-rose p-1" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  {g.type === "video" ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="label-luxe">Type</label>
                          <select
                            data-testid={`ep-gal-type-${i}`}
                            value={g.type}
                            onChange={(e) => updateArr("gallery", i, { ...g, type: e.target.value, url: "" })}
                            className="input-luxe"
                          >
                            <option value="image">Image</option>
                            <option value="video">Video (YouTube / Vimeo)</option>
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <Field
                            label="Video URL (YouTube or Vimeo)"
                            testid={`ep-gal-url-${i}`}
                            value={g.url}
                            onChange={(v) => updateArr("gallery", i, { ...g, url: v })}
                            placeholder="https://youtube.com/watch?v=…"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <label className="label-luxe">Image</label>
                      <GalleryImageUploader
                        value={g.url}
                        onChange={(url) => updateArr("gallery", i, { ...g, url })}
                        testid={`ep-gal-image-${i}`}
                      />
                    </>
                  )}
                  <Field
                    label="Caption (optional)"
                    testid={`ep-gal-caption-${i}`}
                    value={g.caption}
                    onChange={(v) => updateArr("gallery", i, { ...g, caption: v })}
                    placeholder="e.g. Speaking at TEDxBangalore 2024"
                  />
                </div>
              ))}
            </div>
          </section>

          <div className="sticky bottom-0 bg-white border-t border-aubergine/8 -mx-7 px-7 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
            <button type="button" onClick={onClose} className="btn-ghost !py-2.5 !px-5 text-xs">Cancel</button>
            <button type="submit" data-testid="ep-save" disabled={saving} className="btn-primary !py-2.5 !px-6 text-xs">{saving ? "Saving…" : "Save profile"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="label-luxe">{label}</label>
      <input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input-luxe" />
    </div>
  );
}

function GalleryImageUploader({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type || "")) { toast.error("Please pick an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const previewSrc = resolveMediaUrl(value);

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid={testid}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {previewSrc ? (
        <div className="flex items-start gap-4">
          <img src={previewSrc} alt="Uploaded" className="w-40 aspect-video object-cover rounded-lg border border-aubergine/8" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="flex flex-col gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine disabled:opacity-50">
              <Pencil className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Replace"}
            </button>
            <button type="button" onClick={() => onChange("")} className="text-xs text-rose inline-flex items-center gap-1 hover:opacity-80">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={busy}
          className={`w-full rounded-xl border-2 border-dashed px-5 py-8 text-sm text-plumSoft flex flex-col items-center gap-2 transition-colors ${
            dragOver ? "border-aubergine bg-aubergine/[0.04]" : "border-aubergine/20 bg-white/60 hover:border-aubergine/40"
          } disabled:opacity-50`}
        >
          <Plus className={`w-5 h-5 ${dragOver ? "text-aubergine" : "text-plum"}`} />
          <span className="text-[13px]">{busy ? "Uploading…" : "Click or drop image here"}</span>
          <span className="text-[10px] uppercase tracking-widest text-plumSoft/60">JPG · PNG · WEBP · up to 5 MB</span>
        </button>
      )}
    </div>
  );
}

function getEmbedUrl(url) {
  if (!url) return null;
  // YouTube: watch?v=, youtu.be/, /embed/, /shorts/
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function GalleryItem({ item }) {
  const isVideo = item.type === "video";
  const embed = isVideo ? getEmbedUrl(item.url) : null;
  const imgSrc = resolveMediaUrl(item.url);
  return (
    <article className="rounded-xl overflow-hidden border border-aubergine/8 group hover:shadow-luxe transition-all bg-lavender/30">
      <div className="aspect-video bg-black/5 overflow-hidden">
        {isVideo && embed ? (
          <iframe
            src={embed}
            title={item.caption || "Video"}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : isVideo && !embed ? (
          <div className="w-full h-full flex items-center justify-center text-plumSoft text-xs italic px-4 text-center">
            Unsupported video URL — paste a YouTube or Vimeo link
          </div>
        ) : (
          <img src={imgSrc} alt={item.caption || "Gallery visual"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        )}
      </div>
      {item.caption && (
        <div className="p-4">
          <p className="text-sm text-plumSoft font-light leading-relaxed">{item.caption}</p>
        </div>
      )}
    </article>
  );
}

function ShareModal({ member, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/p/${member.id}`;
  const shareText = `Meet ${member.name} — a Proud WOWSHE Member${member.business ? `, ${member.business}` : ""}.`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Public link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Select and copy manually.");
    }
  };

  const enc = encodeURIComponent;
  const channels = [
    { key: "whatsapp", label: "WhatsApp", Icon: MessageSquare, href: `https://wa.me/?text=${enc(`${shareText} ${url}`)}` },
    { key: "linkedin", label: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { key: "twitter", label: "Twitter / X", Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(url)}` },
    { key: "facebook", label: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { key: "email", label: "Email", Icon: Mail, href: `mailto:?subject=${enc(`${member.name} — Proud WOWSHE Member`)}&body=${enc(`${shareText}\n\n${url}`)}` },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-aubergine/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-luxe overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-4 border-b border-aubergine/8 flex items-center justify-between">
          <div>
            <p className="overline">Share profile</p>
            <h2 className="font-display text-2xl text-aubergine mt-1">Share {member.name?.split(" ")[0]}'s profile</h2>
          </div>
          <button onClick={onClose} className="text-plum hover:text-aubergine" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="flex items-center gap-3 p-2 pl-4 rounded-xl border border-aubergine/15 bg-lavender/30">
            <input data-testid="share-link-input" readOnly value={url} className="flex-1 bg-transparent text-sm text-aubergine focus:outline-none truncate" />
            <button onClick={copy} data-testid="copy-link-btn" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-aubergine text-ivory text-xs uppercase tracking-widest hover:bg-aubergineLight">
              {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
            </button>
          </div>

          <div>
            <p className="overline mb-3">Share via</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {channels.map((c) => (
                <a key={c.key} href={c.href} target="_blank" rel="noreferrer" data-testid={`share-${c.key}`} className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-aubergine/15 text-aubergine text-xs font-medium hover:bg-aubergine hover:text-ivory hover:border-aubergine transition-all">
                  <c.Icon className="w-4 h-4 flex-none" />
                  <span className="truncate">{c.label}</span>
                </a>
              ))}
            </div>
          </div>

          <p className="text-xs text-plumSoft text-center pt-2 border-t border-aubergine/8">
            Anyone with this link can view {member.name?.split(" ")[0]}'s public WOWSHE profile. Phone & email stay private.
          </p>
        </div>
      </div>
    </div>
  );
}

